import moment from "moment";
import { API_DATE_FORMAT, DATE_PARSE_FORMATS } from "../constants";
import {
  getBackendEventType,
  getEventEndDateValue,
  getEventStartDateValue,
  getEventTitleValue,
  getEventTypeValue,
} from "../../utils/eventFormatters";

export const isPastDate = (date) =>
  Boolean(date) && date.isBefore(moment().startOf("day"), "day");

export const isBeforeStartDate = (date, startDate) =>
  Boolean(date) && Boolean(startDate) && date.isBefore(startDate);

export const isBeforeStartDateDay = (date, startDate) =>
  Boolean(date) && Boolean(startDate) && date.isBefore(startDate, "day");

export const hasEventStarted = (startDate) =>
  Boolean(startDate) && !startDate.isAfter(moment());

export const validateNotPastDate = (_, value) => {
  if (!isPastDate(value)) {
    return Promise.resolve();
  }

  return Promise.reject(
    new Error("Não é possível selecionar datas no passado."),
  );
};

export const validateEndDateNotBeforeStartDate = ({ getFieldValue }) => ({
  validator(_, value) {
    const startDate = getFieldValue("startDate");

    if (!isBeforeStartDate(value, startDate)) {
      return Promise.resolve();
    }

    return Promise.reject(
      new Error("A data de fim não pode ser anterior à data de início."),
    );
  },
});

export const getDateValue = (date) => {
  if (!date) {
    return null;
  }

  const parsedDate = moment(date, DATE_PARSE_FORMATS, true);

  if (parsedDate.isValid()) {
    return parsedDate;
  }

  const parsedIsoDate = moment(date, moment.ISO_8601, true);

  return parsedIsoDate.isValid() ? parsedIsoDate : null;
};

export const formatApiDateValue = (date) =>
  date ? date.clone().seconds(0).milliseconds(0).format(API_DATE_FORMAT) : "";

export const getJsonValue = (json) => {
  if (json === undefined || json === null) {
    return "";
  }

  return typeof json === "string" ? json : JSON.stringify(json, null, 2);
};

export const parseJsonValue = (json) => {
  const trimmedJson = json?.trim();

  return trimmedJson ? JSON.parse(trimmedJson) : null;
};

export const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const buildLojinhaJsonValue = ({ layout, secret_key, url_iframe }) =>
  JSON.stringify(
    {
      layout: layout || "",
      secret_key: secret_key || "",
      url_iframe: url_iframe || "",
    },
    null,
    2,
  );

export const validateJsonValue = (_, value) => {
  if (!value?.trim()) {
    return Promise.resolve();
  }

  try {
    const parsedJson = JSON.parse(value);

    if (!isPlainObject(parsedJson)) {
      return Promise.reject(new Error("Informe um objeto JSON válido."));
    }

    return Promise.resolve();
  } catch {
    return Promise.reject(new Error("Informe um JSON válido."));
  }
};

export const createEventPayload = (values) => {
  const additionalAttributes = parseJsonValue(values.json);
  const payload = {
    type: getBackendEventType(values.category),
    title: values.name,
    start_date: formatApiDateValue(values.startDate),
    expiration_date: formatApiDateValue(values.endDate),
  };

  if (additionalAttributes) {
    payload.additional_attributes = additionalAttributes;
  }

  return payload;
};

export const getEventStartDatePayloadValue = (event, fallbackDate) =>
  event?.originalEvent?.start_date ||
  formatApiDateValue(getDateValue(event?.originalEvent?.startDate)) ||
  formatApiDateValue(getDateValue(event?.startDate)) ||
  formatApiDateValue(fallbackDate);

export const getEventExpirationDateValue = (event, fallbackDate) =>
  event?.originalEvent?.expiration_date ||
  event?.originalEvent?.end_date ||
  event?.originalEvent?.endDate ||
  formatApiDateValue(fallbackDate);

export const createUpdateEventPayload = (values, event) => {
  const additionalAttributes = parseJsonValue(values.json);
  const eventStartDate = getDateValue(getEventStartDateValue(event));
  const eventStarted = hasEventStarted(eventStartDate);
  const payload = {
    type: getBackendEventType(getEventTypeValue(event)),
    expiration_date: getEventExpirationDateValue(event, values.endDate),
    title: eventStarted ? getEventTitleValue(event) : values.name,
    start_date: eventStarted
      ? getEventStartDatePayloadValue(event, values.startDate)
      : formatApiDateValue(values.startDate),
  };

  if (additionalAttributes) {
    payload.additional_attributes = additionalAttributes;
  }

  return payload;
};

const OFFICIAL_EVENT_FIELDS = new Set([
  "title",
  "name",
  "type",
  "category",
  "start_date",
  "startDate",
  "expiration_date",
  "end_date",
  "endDate",
  "date_created",
  "event_id",
  "eventId",
  "id",
  "displayId",
  "status",
  "participants",
  "participants_count",
  "total_participants",
  "totalParticipants",
  "metrics",
  "additional_attributes",
  "json",
]);

export const getExtraEventAttributes = (originalEvent) => {
  if (!isPlainObject(originalEvent)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(originalEvent).filter(
      ([key, value]) => !OFFICIAL_EVENT_FIELDS.has(key) && value !== undefined,
    ),
  );
};

export const getEventJsonValue = (event) => {
  if (event?.originalEvent?.additional_attributes !== undefined) {
    return event.originalEvent.additional_attributes;
  }

  const extraAttributes = getExtraEventAttributes(event?.originalEvent);

  return Object.keys(extraAttributes).length > 0
    ? extraAttributes
    : event?.json ?? null;
};

export const getFormValuesFromEvent = (event) => ({
  name: event?.name || "",
  category: event?.category || undefined,
  startDate: getDateValue(getEventStartDateValue(event)),
  endDate: getDateValue(getEventEndDateValue(event)),
  json: getJsonValue(getEventJsonValue(event)),
});
